import { useState, ChangeEventHandler, useEffect } from "react";
import { styled } from "@stitches/react";
import { Button, Input, SelectionList, RemovableTag, SelectionListStructuredValue } from "@/vendor/Forge/dist";
import SearchIcon from "@/components/Icons/search";

const Content = styled("div", {
    display: "flex",
    border: "1px solid #B6B7B7",
    borderRadius: "5px",
});

const LeftPanel = styled("div", {
    width: "50%",
    borderRight: "1px solid  #B6B7B7",
    maxHeight: 320,
    overflowY: "scroll",
    padding: "15px",
});

const Search = styled("div", {
    display: "flex",
    gap: 16,
    marginBottom: 16,

    "& svg": {
      position: "absolute",
      top: 8,
      left: 8,
    },
    "& > button": {
      width: 160,
    },
});

const ScrollableContent = styled("div", {
  maxHeight: "240px",
  overflowY: "scroll"
});

const RightPanel = styled("div", {
    width: "50%",
    paddingLeft: 8,
    maxHeight: 320,
    overflowY: "scroll",
    padding: "15px",

    "& .fe_c_removable-tag": {
      width: "fit-content",
    },
});

const Summary = styled("div", {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 16,

    "& > span": {
      fontWeight: "bold",
    },
});

const Tags = styled("div", {
    display: "flex",
    gap: 8,
    flexDirection: "column",
    maxHeight: "240px",
    overflowY: "scroll"
});

interface Props {
  originalList: string[];
  setSelectedValues: (value: string[]) => void;
  selectedValues: string[];
}

const setAllValuesTo = (
    originalList: string[],
    value: boolean,
    prev?: SelectionListStructuredValue,
  ): SelectionListStructuredValue => {
    const reduced = originalList?.reduce((acc, curr) => {
      return { ...acc, [curr]: value };
    }, {});
    return { ...prev, ...reduced  };
  };

  const getSelectedList = (masterList: SelectionListStructuredValue) => {
    return Object.keys(masterList).filter((key) => masterList[key]);
  };

  const convertListToSelectionListStructuredValue = (
    masterList: SelectionListStructuredValue,
  ) => {
    return getSelectedList(masterList).reduce(
      (acc, curr) => ({ ...acc, [curr]: true }),
      {},
    );
  };



const TaoSelector = ({ originalList, setSelectedValues, selectedValues }: Props) => {
    const [masterList, setMasterList] = useState<SelectionListStructuredValue>(
        {}
    );
    const [searchKey, setSearchKey] = useState("");
    const [filteredList, setFilteredList] = useState<string[]>([]);
    const selectedItems = getSelectedList(masterList);

  useEffect(() => {
    setMasterList({ ...setAllValuesTo(originalList, false), ...setAllValuesTo(selectedValues, true) });
    if (searchKey.length > 0) {
      setFilteredList(
        originalList.filter((l) =>
          l.toLowerCase().includes(searchKey.toLocaleLowerCase()),
        )
      );
    } else {
      setFilteredList(originalList);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originalList, selectedValues]);

  useEffect(() => {
    setSearchKey("");
  }, [originalList]);

    const handleItemClick = (
        _:
          | React.MouseEvent<HTMLUListElement, MouseEvent>
          | React.KeyboardEvent<HTMLUListElement>,
        key: string | null,
      ) => {
      if (key === null) return;
      const isSelected = masterList[key];
        setMasterList({
          ...masterList,
          [key]: isSelected,
        });
      if (isSelected) {
        setSelectedValues(selectedItems.filter((item) => item !== key));
        return;
      }
      setSelectedValues([...selectedItems, key]);
      };

  const handleRemoveTag = (key: string) => {
      const isSelected = masterList[key];
        setMasterList({
          ...masterList,
          [key]: isSelected,
        });
        if (isSelected) {
          setSelectedValues(selectedItems.filter((item) => item !== key));
        }
    };

    const handleSearch: ChangeEventHandler<HTMLInputElement> = (e) => {
        const key = e.target.value;
        setSearchKey(key);
        setFilteredList(
          originalList.filter((l) =>
            l.toLowerCase().includes(key.toLocaleLowerCase()),
          ),
        );
      };

    // const handleSelectAllClick = () => {
    //       if (searchKey !== "") {
    //         setFilteredList(
    //           originalList.filter((l) =>
    //             l.toLowerCase().includes(searchKey.toLocaleLowerCase()),
    //           ),
    //         );
    //       }
    //   setMasterList(prev => setAllValuesTo(filteredList, true, prev));
    // };

    const handleClearAllClick = () => {
        setMasterList(setAllValuesTo(originalList, false));
        setSelectedValues([]);
      };

    return (
        <Content>
                    <LeftPanel>
                    <Search>
                        <Input
                        placeholder="Search"
                        icon={SearchIcon}
                        onChange={handleSearch}
                        value={searchKey}
                        />
                        {/* <Button
                            variant="tertiary"
                            onClick={handleSelectAllClick}
                        >
                        Select All
                        </Button> */}
                    </Search>
                    <ScrollableContent>
                    <SelectionList
                        options={filteredList}
                        onClickItem={handleItemClick}
                        value={convertListToSelectionListStructuredValue(masterList)}
                    />
                    </ScrollableContent>
                    </LeftPanel>
                    <RightPanel>
                    <Summary>
                        <span>Selected({selectedItems.length})</span>
                        <Button
                            variant="tertiary"
                            onClick={handleClearAllClick}
                        >
                        Clear All
                        </Button>
                    </Summary>
                    <Tags>
                        {selectedItems.map((selectedItem) => (
                        <RemovableTag
                            key={selectedItem}
                            text={selectedItem}
                            data-testid="removable-tag"
                            onRemove={() => handleRemoveTag(selectedItem)}
                        />
                        ))}
                    </Tags>
                    </RightPanel>
                </Content>
    );
};

export default TaoSelector;
